import { Fragment, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useLessons,
  useCreateLesson,
  useUpdateLesson,
  useDeleteLesson,
  useStudents,
  useTeachers,
  useUpdateStudent,
  useDeleteStudent,
  useBlockStudent,
  useUnblockStudent,
  useStudentNotes,
  useCreateStudentNote,
  lessonFieldErrors,
  type LessonInput,
  type StudentChanges,
} from "@/api/queries";
import { Badge, Button, Card, Input, SegmentedControl, Switch } from "@/design-system";
import { Icon } from "@/components/Icon";
import { StudentPicker, StudentPickerDialog } from "@/components/StudentPicker";
import { LessonDetailSheet } from "./LessonDetailSheet";
import { StudentDetailSheet } from "./StudentDetailSheet";
import { useAuth } from "@/auth";
import {
  formatDays,
  formatDuration,
  formatFullDate,
  formatLongDate,
  formatMonth,
  formatTime,
} from "@/lib/format";
import {
  fromZonedDatetimeLocal,
  getStudioTimezone,
  toZonedDatetimeLocal,
  zonedDateKey,
  zonedMonthKey,
  zonedParts,
  zonedStartOfDay,
  zonedEndOfDay,
  addLocalDays,
  zonedWallTimeToUtc,
} from "@/lib/timezone";
import { instructorLabel } from "@/lib/instructor";
import { displayName, matchesUserSearch } from "@/lib/user";
import { useOnline } from "@/lib/useOnline";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import { SHEET_OVERLAY_STYLE, SHEET_STYLE } from "@/components/sheetStyles";
import { RefreshButton } from "@/components/RefreshButton";
import screen from "@/layout/screen.module.css";
import type { Lesson, Student, StudentNote } from "@/lib/types";

/**
 * Teacher dashboard — the studio's lesson-management home for
 * `/teacher`, guarded by `<RequireRole role="teacher">` in `App.tsx`.
 *
 * Data layer is the *same* `GET /api/lessons` the student calendar
 * uses (`useLessons`), but rendered in edit mode. The schedule has two
 * browsing modes (issue #325): **day** (the default) fetches a single
 * studio-local day, while **month** fetches one calendar month at a
 * time and pages month by month, so the pager always reads as a single
 * month and never as a rolling multi-month window (issue #265). A
 * "Today" shortcut jumps back to the current day/month from anywhere.
 * Lessons are grouped by studio-local day; every lesson shows its
 * `spots_taken / capacity` fill rate and carries Edit / Delete controls
 * instead of a Book CTA.
 *
 * Mutations go through the react-query hooks in `api/queries.ts`:
 *   - Create:  POST   /api/lessons        (`useCreateLesson`)
 *   - Edit:    PATCH  /api/lessons/:id     (`useUpdateLesson`)
 *   - Delete:  DELETE /api/lessons/:id     (`useDeleteLesson`)
 * Each invalidates the `lessons` cache on success, so a created /
 * edited / deleted lesson is reflected immediately. The new lesson's
 * `instructor_id` defaults to the logged-in teacher.
 *
 * Validation failures from the API (`422` with a field-keyed body) are
 * rendered inline beneath the offending field.
 */

interface ProgramDay {
  /** "YYYY-MM-DD" in the studio timezone. */
  key: string;
  /** The instant of local noon on that day, for formatting. */
  date: Date;
  /** "YYYY-MM" of the day's month. */
  monthKey: string;
  lessons: Lesson[];
}

/** Schedule browsing granularity (issue #325). */
type ScheduleView = "day" | "month";

/** Local noon on the first day of the month containing `date`. */
function monthAnchor(date: Date): Date {
  const { year, month } = zonedParts(date);
  return zonedWallTimeToUtc({ year, month, day: 1, hour: 12 });
}

/** Shifts a month anchor by `months`, staying at local noon on day 1. */
function shiftMonths(anchor: Date, months: number): Date {
  const { year, month } = zonedParts(anchor);
  const total = year * 12 + (month - 1) + months;
  return zonedWallTimeToUtc({
    year: Math.floor(total / 12),
    month: (((total % 12) + 12) % 12) + 1,
    day: 1,
    hour: 12,
  });
}

/** Local noon on the studio-local day containing `date` (day-view anchor). */
function dayAnchor(date: Date): Date {
  const { year, month, day } = zonedParts(date);
  return zonedWallTimeToUtc({ year, month, day, hour: 12 });
}

/** Shifts a day anchor by `days`, staying at local noon. */
function shiftDays(anchor: Date, days: number): Date {
  const { year, month, day } = zonedParts(anchor);
  const shifted = addLocalDays({ year, month, day }, days);
  return zonedWallTimeToUtc({ ...shifted, hour: 12 });
}

/** Buckets lessons into studio-local calendar days, ascending. */
function groupLessonsByDay(lessons: Lesson[]): ProgramDay[] {
  const timezone = getStudioTimezone();
  const groups = new Map<string, ProgramDay>();

  for (const lesson of lessons) {
    const date = new Date(lesson.starts_at);
    const key = zonedDateKey(date, timezone);
    const existing = groups.get(key);

    if (existing) {
      existing.lessons.push(lesson);
    } else {
      groups.set(key, {
        key,
        date,
        monthKey: zonedMonthKey(date, timezone),
        lessons: [lesson],
      });
    }
  }

  return [...groups.values()].sort((a, b) => a.key.localeCompare(b.key));
}

interface FormValues {
  title: string;
  description: string;
  /** Lesson type (issue #183): `:open` self-booking or `:assigned`.
   *  Create-time only — read-only when editing. */
  lesson_type: "open" | "assigned";
  /** Selected students to enroll at create time — both lesson types
   *  (issues #183/#259). Empty when editing. */
  student_ids: number[];
  /** Gift the selected `:open` seats instead of spending credits. */
  comp: boolean;
  /** Chosen instructor (issue #284). Defaults to the logged-in teacher on
   *  create, or the lesson's current instructor on edit. */
  instructor_id: number | null;
  /** `<input type="datetime-local">` value, e.g. "2026-06-23T10:00". */
  starts_at: string;
  duration_minutes: string;
  capacity: string;
  /** Minutes before start that bookings close; empty = global default. */
  booking_cutoff_minutes: string;
  /** Days before start that bookings open; empty = open immediately. */
  booking_open_days: string;
  /** Weekly occurrences; empty = one-off lesson (issue #59). */
  recurrence_occurrences: string;
}

const EMPTY_FORM: FormValues = {
  title: "",
  description: "",
  lesson_type: "open",
  student_ids: [],
  comp: false,
  instructor_id: null,
  starts_at: "",
  duration_minutes: "55",
  capacity: "8",
  booking_cutoff_minutes: "",
  booking_open_days: "",
  recurrence_occurrences: "",
};

/** Create vs. edit, carrying the lesson being edited. */
type FormMode = { type: "create" } | { type: "edit"; lesson: Lesson };

function valuesFromLesson(lesson: Lesson): FormValues {
  return {
    title: lesson.title,
    description: lesson.description ?? "",
    // The type is create-time only; edit shows it read-only.
    lesson_type: lesson.lesson_type,
    // Enrollments are managed through the Students dialog, not the form.
    student_ids: [],
    comp: false,
    instructor_id: lesson.instructor_id,
    starts_at: toZonedDatetimeLocal(lesson.starts_at),
    duration_minutes: String(lesson.duration_minutes),
    capacity: String(lesson.capacity),
    // The API resolves the default (60), so prefilling it is honest;
    // clearing the field sends null and returns to the default.
    booking_cutoff_minutes: String(lesson.booking_cutoff_minutes),
    // null (open immediately) is the absence of a restriction.
    booking_open_days: lesson.booking_open_days === null ? "" : String(lesson.booking_open_days),
    // Recurrence is create-time only; an edited instance stays one-off.
    recurrence_occurrences: "",
  };
}

/** Turn the string-backed form into the typed API payload. */
function buildPayload(values: FormValues, instructorId: number): LessonInput {
  const description = values.description.trim();
  const cutoffRaw = values.booking_cutoff_minutes.trim();
  const openDaysRaw = values.booking_open_days.trim();
  const recurrenceRaw = values.recurrence_occurrences.trim();

  return {
    title: values.title.trim(),
    description: description === "" ? null : description,
    lesson_type: values.lesson_type,
    starts_at: fromZonedDatetimeLocal(values.starts_at),
    duration_minutes: Number(values.duration_minutes),
    capacity: Number(values.capacity),
    booking_cutoff_minutes:
      cutoffRaw === "" ? null : Math.max(0, Math.floor(Number(cutoffRaw))),
    booking_open_days:
      openDaysRaw === "" ? null : Math.max(0, Math.floor(Number(openDaysRaw))),
    recurrence:
      recurrenceRaw === ""
        ? null
        : { occurrences: Math.min(52, Math.max(1, Math.floor(Number(recurrenceRaw)))) },
    // Create-time enrollment (issues #183/#259): both lesson types accept
    // it; `:assigned` seats are membership-covered, `:open` seats spend a
    // credit unless comped.
    ...(values.student_ids.length > 0 ? { student_ids: values.student_ids } : {}),
    ...(values.lesson_type === "open" && values.student_ids.length > 0 && values.comp
      ? { comp: true }
      : {}),
    instructor_id: values.instructor_id ?? instructorId,
  };
}

export function TeacherScreen() {
  const { currentUser, token, logout } = useAuth();
  const { t } = useTranslation();

  const now = useMemo(() => new Date(), []);

  // Day view is the default (issue #325): a teacher usually wants the
  // handful of classes happening today, not a whole month at once.
  const [viewMode, setViewMode] = useState<ScheduleView>("day");
  const isDayView = viewMode === "day";
  // Anchor is studio-local noon on a focus date. Day view reads its
  // calendar day, month view reads its calendar month, so toggling the
  // mode keeps the teacher on the period they were looking at.
  const [anchor, setAnchor] = useState<Date>(() => dayAnchor(now));

  const range = useMemo(() => {
    if (viewMode === "day") {
      // Exactly one studio-local calendar day.
      return {
        from: zonedStartOfDay(anchor).toISOString(),
        to: zonedEndOfDay(anchor).toISOString(),
      };
    }

    // Exactly one calendar month: first day through last day, studio-local.
    const monthStart = monthAnchor(anchor);
    // One millisecond before the studio-local start of the next month is
    // the end of the last day of the anchor month (issue #265).
    const nextMonthStart = zonedStartOfDay(shiftMonths(monthStart, 1));

    return {
      from: zonedStartOfDay(monthStart).toISOString(),
      to: new Date(nextMonthStart.getTime() - 1).toISOString(),
    };
  }, [anchor, viewMode]);

  /** Move the pager one day (Day view) or one month (Month view). */
  function shiftPeriod(delta: number) {
    setAnchor((a) => (isDayView ? shiftDays(a, delta) : shiftMonths(a, delta)));
  }

  /** Jump back to the current studio-local day / month. */
  function goToToday() {
    setAnchor(dayAnchor(new Date()));
  }

  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [view, setView] = useState<"schedule" | "students">("schedule");
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  // The lesson whose enrollment dialog is open (issue #186).
  const [studentsLesson, setStudentsLesson] = useState<Lesson | null>(null);
  // The lesson whose detail sheet is open (issue #222).
  const [detailLesson, setDetailLesson] = useState<Lesson | null>(null);
  // The student whose detail sheet is open (issue #223).
  const [detailStudent, setDetailStudent] = useState<Student | null>(null);
  const online = useOnline();

  const lessonsQuery = useLessons(token, range);
  const createMutation = useCreateLesson(token);
  const updateMutation = useUpdateLesson(token);
  const deleteMutation = useDeleteLesson(token);
  const studentsQuery = useStudents(token, view === "students");
  const updateStudentMutation = useUpdateStudent(token);
  const deleteStudentMutation = useDeleteStudent(token);
  const blockStudentMutation = useBlockStudent(token);
  const unblockStudentMutation = useUnblockStudent(token);

  const lessons: Lesson[] = lessonsQuery.data ?? [];
  const programDays = useMemo(() => groupLessonsByDay(lessons), [lessons]);

  // The form is bound to whichever mutation is active for the mode.
  const formMutation =
    formMode?.type === "edit" ? updateMutation : createMutation;
  const fieldErrs = lessonFieldErrors(formMutation.error);
  // A non-field error (e.g. 403/500, or a `detail`-shaped 404) still
  // deserves a line at the top of the form.
  const formError =
    formMutation.isError && Object.keys(fieldErrs).length === 0
      ? formMutation.error?.message ?? t("teacher.saveError")
      : null;

  function openCreate() {
    createMutation.reset();
    updateMutation.reset();
    setConfirmId(null);
    setFormMode({ type: "create" });
  }

  function openEdit(lesson: Lesson) {
    createMutation.reset();
    updateMutation.reset();
    setConfirmId(null);
    setFormMode({ type: "edit", lesson });
  }

  function closeForm() {
    createMutation.reset();
    updateMutation.reset();
    setFormMode(null);
  }

  function handleSubmit(values: FormValues) {
    const payload = buildPayload(values, currentUser?.id ?? 0);
    if (formMode?.type === "edit") {
      updateMutation.mutate(
        { id: formMode.lesson.id, changes: payload },
        { onSuccess: closeForm },
      );
    } else {
      createMutation.mutate(payload, { onSuccess: closeForm });
    }
  }

  function requestDelete(id: number) {
    deleteMutation.reset();
    setConfirmId(id);
  }

  function confirmDelete(id: number) {
    deleteMutation.mutate(id, {
      onSuccess: () => setConfirmId(null),
    });
  }

  const teacherName = currentUser
    ? displayName(currentUser)
    : t("teacher.greetingFallbackName");

  return (
    <div className={screen.capped} data-testid="teacher-screen" style={{ paddingBottom: 96 }}>
      {/* header */}
      <div style={{ padding: "20px 20px 8px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-xs)/1 var(--font-body)",
                color: "var(--text-faint)",
              }}
            >
              {t("teacher.eyebrow")}
            </div>
            <div
              style={{
                font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
                color: "var(--text-strong)",
                letterSpacing: "var(--ls-tight)",
                marginTop: 4,
                textTransform: "capitalize",
              }}
            >
              {t("teacher.greeting", { name: teacherName })}
            </div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-xs)/1.4 var(--font-body)",
                color: "var(--text-muted)",
                marginTop: 4,
              }}
            >
              {t("teacher.signedInAs")} <strong>{currentUser?.email}</strong>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => logout()}
            data-testid="teacher-logout"
          >
            {t("common.signOut")}
          </Button>
        </div>

        <div style={{ marginTop: 16 }}>
          <SegmentedControl
            size="sm"
            options={[
              { label: t("teacher.scheduleTab"), value: "schedule" },
              { label: t("teacher.studentsTab"), value: "students" },
            ]}
            value={view}
            onChange={(v) => setView(v as "schedule" | "students")}
          />
        </div>

        {view === "schedule" && (
          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Button
              fullWidth
              onClick={openCreate}
              data-testid="teacher-new-lesson"
              iconLeft={<Icon name="calendar" size={18} />}
              style={{ width: "auto", flex: 1 }}
            >
              {t("teacher.newClass")}
            </Button>
            <RefreshButton
              onClick={() => lessonsQuery.refetch()}
              refreshing={lessonsQuery.isFetching}
              testId="teacher-refresh-lessons"
            />
          </div>
        )}
      </div>

      {view === "schedule" ? (
        <>
      {/* view controls (issue #325): Day/Month toggle + Today shortcut */}
      <div
        data-testid="teacher-program-controls"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "12px 20px 0",
        }}
      >
        <SegmentedControl
          size="sm"
          options={[
            { label: t("teacher.programViewDay"), value: "day" },
            { label: t("teacher.programViewMonth"), value: "month" },
          ]}
          value={viewMode}
          onChange={(v) => setViewMode(v as ScheduleView)}
        />
        <Button
          variant="ghost"
          size="sm"
          onClick={goToToday}
          data-testid="teacher-program-today"
          iconLeft={<Icon name="calendar" size={16} />}
        >
          {t("teacher.programToday")}
        </Button>
      </div>
      {/* program pager — one day or one month at a time */}
      <div
        data-testid="teacher-program-nav"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "8px 20px 0",
        }}
      >
        <button
          type="button"
          aria-label={
            isDayView
              ? t("teacher.programPrevDay")
              : t("teacher.programPrevMonth")
          }
          data-testid="teacher-program-prev"
          onClick={() => shiftPeriod(-1)}
          style={{
            flex: "none",
            width: 36,
            height: 36,
            borderRadius: "var(--radius-pill)",
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-card)",
            color: "var(--text-strong)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "var(--shadow-xs)",
          }}
        >
          <Icon name="arrow-left" size={18} />
        </button>
        <div
          data-testid="teacher-program-range"
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: "center",
            font: "var(--fw-semibold) var(--fs-sm)/1.35 var(--font-body)",
            color: "var(--text-strong)",
          }}
        >
          {isDayView
            ? formatLongDate(anchor)
            : `${formatFullDate(range.from)} – ${formatFullDate(range.to)}`}
        </div>
        <button
          type="button"
          aria-label={
            isDayView
              ? t("teacher.programNextDay")
              : t("teacher.programNextMonth")
          }
          data-testid="teacher-program-next"
          onClick={() => shiftPeriod(1)}
          style={{
            flex: "none",
            width: 36,
            height: 36,
            borderRadius: "var(--radius-pill)",
            border: "1px solid var(--border-subtle)",
            background: "var(--surface-card)",
            color: "var(--text-strong)",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "var(--shadow-xs)",
          }}
        >
          <Icon name="arrow-right" size={18} />
        </button>
      </div>
      {/* lesson list */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 12,
          padding: "12px 20px",
        }}
      >
        {lessonsQuery.isLoading && (
          <div
            data-testid="teacher-loading"
            style={{
              padding: "var(--space-6)",
              textAlign: "center",
              color: "var(--text-faint)",
            }}
          >
            {t("teacher.loading")}
          </div>
        )}

        {!lessonsQuery.isLoading && lessonsQuery.isError && (
          online ? (
            <div
              role="alert"
              data-testid="teacher-error"
              style={{
                background: "var(--status-danger-bg)",
                color: "var(--status-danger-fg)",
                padding: "var(--space-4)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              {lessonsQuery.error?.message ?? t("teacher.error")}
            </div>
          ) : (
            <OfflineErrorState />
          )
        )}

        {!lessonsQuery.isLoading &&
          !lessonsQuery.isError &&
          lessons.length === 0 && (
            <div
              data-testid="teacher-empty"
              style={{
                padding: "var(--space-8) var(--space-4)",
                textAlign: "center",
                color: "var(--text-muted)",
              }}
            >
              <div
                style={{
                  font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
                  color: "var(--text-strong)",
                  marginBottom: 8,
                }}
              >
                {isDayView ? t("teacher.emptyDayTitle") : t("teacher.emptyTitle")}
              </div>
              <div
                style={{
                  font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
                }}
              >
                {isDayView ? t("teacher.emptyDayBody") : t("teacher.emptyBody")}
              </div>
            </div>
          )}

        {!lessonsQuery.isLoading &&
          !lessonsQuery.isError &&
          programDays.map((day, index) => {
            // A single day needs no month banner — the pager already
            // names the day.
            const showMonth =
              !isDayView &&
              (index === 0 || programDays[index - 1].monthKey !== day.monthKey);
            return (
              <Fragment key={day.key}>
                {showMonth && (
                  <div
                    data-testid={`teacher-month-${day.monthKey}`}
                    style={{
                      font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
                      color: "var(--text-strong)",
                      paddingTop: index === 0 ? 0 : "var(--space-2)",
                    }}
                  >
                    {formatMonth(day.date)}
                  </div>
                )}
                <div
                  data-testid={`teacher-day-${day.key}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                        letterSpacing: "var(--ls-wide)",
                        textTransform: "uppercase",
                        color: "var(--text-faint)",
                      }}
                    >
                      {formatLongDate(day.date)}
                    </span>
                    <span
                      data-testid={`teacher-day-${day.key}-count`}
                      style={{
                        font: "var(--fw-medium) var(--fs-2xs)/1 var(--font-mono)",
                        color: "var(--text-faint)",
                      }}
                    >
                      {t("teacher.dayCount", { count: day.lessons.length })}
                    </span>
                  </div>
                  {day.lessons.map((lesson) => (
                    <TeacherLessonRow
                      key={lesson.id}
                      lesson={lesson}
                      confirming={confirmId === lesson.id}
                      deleting={
                        deleteMutation.isPending && deleteMutation.variables === lesson.id
                      }
                      deleteError={
                        confirmId === lesson.id && deleteMutation.isError
                          ? deleteMutation.error?.message ?? t("teacher.deleteError")
                          : null
                      }
                      onOpenDetails={() => setDetailLesson(lesson)}
                      onEdit={() => openEdit(lesson)}
                      onManageStudents={() => setStudentsLesson(lesson)}
                      onRequestDelete={() => requestDelete(lesson.id)}
                      onCancelDelete={() => setConfirmId(null)}
                      onConfirmDelete={() => confirmDelete(lesson.id)}
                    />
                  ))}
                </div>
              </Fragment>
            );
          })}
      </div>
        </>
      ) : (
        <StudentRoster
          studentsQuery={studentsQuery}
          onOpenStudent={(student) => {
            updateStudentMutation.reset();
            deleteStudentMutation.reset();
            blockStudentMutation.reset();
            unblockStudentMutation.reset();
            setDetailStudent(student);
          }}
          onEditStudent={setEditingStudent}
        />
      )}

      {/* create / edit form overlay */}
      {formMode && (
        <LessonFormOverlay
          // Remounting on mode change re-seeds the field state.
          key={formMode.type === "edit" ? `edit-${formMode.lesson.id}` : "create"}
          mode={formMode.type}
          token={token}
          heading={formMode.type === "edit" ? t("teacher.headingEdit") : t("teacher.headingNew")}
          submitLabel={formMode.type === "edit" ? t("teacher.submitSave") : t("teacher.submitCreate")}
          initial={
            formMode.type === "edit"
              ? valuesFromLesson(formMode.lesson)
              : { ...EMPTY_FORM, instructor_id: currentUser?.id ?? null }
          }
          enrolledCount={formMode.type === "edit" ? formMode.lesson.spots_taken : 0}
          busy={formMutation.isPending}
          fieldErrors={fieldErrs}
          formError={formError}
          onCancel={closeForm}
          onSubmit={handleSubmit}
        />
      )}

      {/* enrollment dialog for an :assigned lesson (issue #186) */}
      {studentsLesson && (
        <StudentPickerDialog
          token={token}
          lesson={studentsLesson}
          onClose={() => setStudentsLesson(null)}
        />
      )}

      {/* read-oriented detail sheet for any lesson (issue #222) */}
      {detailLesson && (
        <LessonDetailSheet
          lesson={detailLesson}
          token={token}
          onClose={() => setDetailLesson(null)}
          onManageStudents={() => {
            setStudentsLesson(detailLesson);
            setDetailLesson(null);
          }}
          onOpenStudent={(student) => {
            updateStudentMutation.reset();
            deleteStudentMutation.reset();
            blockStudentMutation.reset();
            unblockStudentMutation.reset();
            setDetailStudent(student);
          }}
        />
      )}

      {/* read-oriented detail sheet for a student (issue #223) */}
      {detailStudent && (
        <StudentDetailSheet
          student={detailStudent}
          token={token}
          deleting={deleteStudentMutation.isPending}
          deleteError={
            deleteStudentMutation.isError
              ? deleteStudentMutation.error?.message ?? t("teacher.studentDeleteError")
              : null
          }
          blocking={blockStudentMutation.isPending || unblockStudentMutation.isPending}
          blockError={
            blockStudentMutation.isError
              ? (blockStudentMutation.error?.message ?? t("teacher.studentBlockError"))
              : unblockStudentMutation.isError
                ? (unblockStudentMutation.error?.message ?? t("teacher.studentUnblockError"))
                : null
          }
          onClose={() => setDetailStudent(null)}
          onEdit={() => {
            updateStudentMutation.reset();
            setEditingStudent(detailStudent);
          }}
          onBlock={(reason) => {
            blockStudentMutation.mutate(
              { studentId: detailStudent.id, reason: reason || undefined },
              {
                onSuccess: (updated) => {
                  blockStudentMutation.reset();
                  setDetailStudent(updated);
                },
              },
            );
          }}
          onUnblock={() => {
            unblockStudentMutation.mutate(detailStudent.id, {
              onSuccess: (updated) => {
                unblockStudentMutation.reset();
                setDetailStudent(updated);
              },
            });
          }}
          onDelete={() => {
            deleteStudentMutation.mutate(detailStudent.id, {
              onSuccess: () => {
                deleteStudentMutation.reset();
                setDetailStudent(null);
              },
            });
          }}
        />
      )}

      {/* student edit overlay (issue #88) */}
      {editingStudent && (
        <StudentEditOverlay
          key={`student-${editingStudent.id}`}
          student={editingStudent}
          token={token}
          busy={updateStudentMutation.isPending}
          fieldErrors={lessonFieldErrors(updateStudentMutation.error)}
          formError={
            updateStudentMutation.isError &&
            Object.keys(lessonFieldErrors(updateStudentMutation.error)).length === 0
              ? updateStudentMutation.error?.message ?? t("teacher.studentSaveError")
              : null
          }
          onCancel={() => {
            updateStudentMutation.reset();
            setEditingStudent(null);
          }}
          onSubmit={(changes) =>
            updateStudentMutation.mutate(
              { id: editingStudent.id, changes },
              {
                onSuccess: (updated) => {
                  setEditingStudent(null);
                  // Keep an open detail sheet in sync with the edit.
                  setDetailStudent((current) =>
                    current && current.id === updated.id ? updated : current,
                  );
                },
              },
            )
          }
        />
      )}
    </div>
  );
}

/** A single lesson in edit mode: fill rate + Edit / Delete controls. */
function TeacherLessonRow({
  lesson,
  confirming,
  deleting,
  deleteError,
  onOpenDetails,
  onEdit,
  onManageStudents,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
}: {
  lesson: Lesson;
  confirming: boolean;
  deleting: boolean;
  deleteError: string | null;
  onOpenDetails: () => void;
  onEdit: () => void;
  onManageStudents: () => void;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
}) {
  const { t } = useTranslation();
  const pct =
    lesson.capacity > 0
      ? Math.min(Math.round((lesson.spots_taken / lesson.capacity) * 100), 100)
      : 0;
  const full = lesson.spots_left <= 0;
  // Allowed on purpose (see issue #183): the teacher may shrink a class
  // below its current headcount, so we warn instead of blocking.
  const overCapacity = lesson.spots_taken > lesson.capacity;

  return (
    <Card
      data-testid={`teacher-lesson-${lesson.id}`}
      padding="var(--space-4)"
      interactive
      role="button"
      tabIndex={0}
      aria-label={t("teacher.detailOpenAria", { title: lesson.title })}
      onClick={() => {
        if (!confirming) onOpenDetails();
      }}
      onKeyDown={(e) => {
        if (confirming) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenDetails();
        }
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
              color: "var(--text-faint)",
            }}
          >
            {formatLongDate(lesson.starts_at)}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              marginTop: 4,
            }}
          >
            <span
              style={{
                font: "var(--fw-bold) var(--fs-lg)/1 var(--font-mono)",
                color: "var(--text-strong)",
              }}
            >
              {formatTime(lesson.starts_at)}
            </span>
            <span
              style={{
                font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
                color: "var(--text-faint)",
              }}
            >
              {formatDuration(lesson.duration_minutes)}
            </span>
          </div>
          <div
            data-testid={`teacher-lesson-${lesson.id}-title-row`}
            style={{
              display: "flex",
              alignItems: "center",
              // The title must never be squeezed away by the badges: when the
              // "assigned" pill cannot share the line, it wraps below instead.
              flexWrap: "wrap",
              gap: 8,
              marginTop: 6,
              minWidth: 0,
            }}
          >
            <span
              style={{
                font: "var(--fw-bold) var(--fs-lg)/1.1 var(--font-display)",
                color: "var(--text-strong)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: "6ch",
                flexShrink: 1,
              }}
            >
              {lesson.title}
            </span>
            {lesson.recurrence_key && (
              <Badge
                tone="neutral"
                data-testid={`teacher-lesson-${lesson.id}-series`}
              >
                {t("teacher.seriesTag")}
              </Badge>
            )}
            {lesson.lesson_type === "assigned" && (
              <Badge
                tone="info"
                data-testid={`teacher-lesson-${lesson.id}-type`}
              >
                {t("teacher.typeAssigned")}
              </Badge>
            )}
          </div>
          <div
            style={{
              font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
              color: "var(--text-muted)",
              marginTop: 4,
            }}
          >
            {instructorLabel(lesson)}
          </div>
          <div
            data-testid={`teacher-lesson-${lesson.id}-cutoff`}
            style={{
              font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
              color: "var(--text-faint)",
              marginTop: 4,
            }}
          >
            {lesson.booking_open_days !== null && lesson.booking_open_days !== undefined
              ? t("teacher.windowMeta", {
                  open: formatDays(lesson.booking_open_days),
                  close: formatDuration(lesson.booking_cutoff_minutes),
                })
              : t("teacher.cutoffMeta", {
                  duration: formatDuration(lesson.booking_cutoff_minutes),
                })}
          </div>
        </div>

        <Badge tone={full ? "danger" : "success"} dot>
          {full ? t("teacher.full") : t("teacher.open", { count: lesson.spots_left })}
        </Badge>
      </div>

      {/* fill rate */}
      <div style={{ marginTop: 14 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 6,
          }}
        >
          <span
            style={{
              font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
              letterSpacing: "var(--ls-wide)",
              textTransform: "uppercase",
              color: "var(--text-faint)",
            }}
          >
            {t("teacher.fillRate")}
          </span>
          <span
            data-testid={`teacher-lesson-${lesson.id}-fill`}
            style={{
              font: "var(--fw-bold) var(--fs-sm)/1 var(--font-mono)",
              color: "var(--text-strong)",
            }}
          >
            {t("teacher.fill", { taken: lesson.spots_taken, capacity: lesson.capacity })}
          </span>
        </div>
        <div
          aria-hidden
          style={{
            height: 8,
            borderRadius: 999,
            background: "var(--surface-muted)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: "100%",
              borderRadius: 999,
              background: full ? "var(--rust-500)" : "var(--brand)",
              transition: "width var(--dur-base) var(--ease-out)",
            }}
          />
        </div>
      </div>

      {/* controls */}
      {overCapacity && (
        <div
          role="alert"
          data-testid={`teacher-lesson-${lesson.id}-over-capacity`}
          style={{
            marginTop: 12,
            padding: "var(--space-2) var(--space-4)",
            background: "var(--status-warning-bg)",
            color: "var(--status-warning-fg)",
            borderRadius: "var(--radius-md)",
            font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
          }}
        >
          {t("teacher.overCapacity", {
            enrolled: lesson.spots_taken,
            capacity: lesson.capacity,
          })}
        </div>
      )}

      {!confirming ? (
        <div
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            gap: 8,
            marginTop: 14,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="secondary"
            size="sm"
            onClick={onManageStudents}
            data-testid={`teacher-lesson-${lesson.id}-students`}
          >
            {t("teacher.students")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onEdit}
            data-testid={`teacher-lesson-${lesson.id}-edit`}
          >
            {t("teacher.edit")}
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onRequestDelete}
            data-testid={`teacher-lesson-${lesson.id}-delete`}
          >
            {t("teacher.delete")}
          </Button>
        </div>
      ) : (
        <div
          data-testid={`teacher-lesson-${lesson.id}-confirm`}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{
            marginTop: 14,
            padding: "var(--space-3) var(--space-4)",
            background: "var(--status-danger-bg)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <div
            style={{
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              color: "var(--status-danger-fg)",
            }}
          >
            {t("teacher.confirmDelete", { title: lesson.title })}
          </div>
          {deleteError && (
            <div
              role="alert"
              data-testid={`teacher-lesson-${lesson.id}-delete-error`}
              style={{
                font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
                color: "var(--status-danger-fg)",
                marginTop: 6,
              }}
            >
              {deleteError}
            </div>
          )}
          <div
            style={{
              display: "flex",
              gap: 8,
              marginTop: 10,
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant="secondary"
              size="sm"
              onClick={onCancelDelete}
              disabled={deleting}
              data-testid={`teacher-lesson-${lesson.id}-cancel-delete`}
            >
              {t("teacher.keep")}
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={onConfirmDelete}
              disabled={deleting}
              data-testid={`teacher-lesson-${lesson.id}-confirm-delete`}
            >
              {deleting ? t("teacher.deleting") : t("teacher.deleteClass")}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/** Modal-ish overlay wrapping the create / edit form. */
function LessonFormOverlay({
  mode,
  token,
  heading,
  submitLabel,
  initial,
  enrolledCount,
  busy,
  fieldErrors,
  formError,
  onCancel,
  onSubmit,
}: {
  mode: "create" | "edit";
  token: string | null;
  heading: string;
  submitLabel: string;
  initial: FormValues;
  /** Already-enrolled headcount (edit only) for the over-capacity warning. */
  enrolledCount: number;
  busy: boolean;
  fieldErrors: Record<string, string[]>;
  formError: string | null;
  onCancel: () => void;
  onSubmit: (values: FormValues) => void;
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState<FormValues>(initial);

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  function submit() {
    if (busy) return;
    onSubmit(values);
  }

  // The student picker (create only) reuses the presentational
  // `StudentPicker`; the roster is only fetched while it is on screen.
  const wantsPicker = mode === "create";
  const studentsQuery = useStudents(token, wantsPicker);
  const allStudents = studentsQuery.data ?? [];
  // The instructor picker is always on screen (create and edit), so the
  // teacher list is fetched whenever the form is open (issue #284).
  const teachersQuery = useTeachers(token);
  const teachers = teachersQuery.data ?? [];
  const selectedStudents = allStudents.filter((s) =>
    values.student_ids.includes(s.id),
  );
  const capacityValue = Number(values.capacity);
  const pickerAtCapacity =
    Number.isFinite(capacityValue) && selectedStudents.length >= capacityValue;
  const overCapacity =
    mode === "edit" && enrolledCount > Number(values.capacity);
  const typeLabel =
    values.lesson_type === "assigned"
      ? t("teacher.typeAssigned")
      : t("teacher.typeOpen");

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={heading}
      data-testid="lesson-form"
      style={SHEET_OVERLAY_STYLE}
      onClick={onCancel}
    >
      <Card
        padding="var(--space-5)"
        onClick={(e) => e.stopPropagation()}
        style={SHEET_STYLE}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "var(--space-4)",
          }}
        >
          <div
            style={{
              font: "var(--fw-extra) var(--fs-xl)/1.1 var(--font-display)",
              color: "var(--text-strong)",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            {heading}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid="lesson-form-cancel"
          >
            {t("common.cancel")}
          </Button>
        </div>

        {formError && (
          <div
            role="alert"
            data-testid="lesson-form-error"
            style={{
              background: "var(--status-danger-bg)",
              color: "var(--status-danger-fg)",
              padding: "var(--space-3) var(--space-4)",
              borderRadius: "var(--radius-md)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              marginBottom: "var(--space-4)",
            }}
          >
            {formError}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <div data-testid="lesson-form-type">
            <div
              style={{
                font: "var(--text-label)",
                color: "var(--text-body)",
                marginBottom: 6,
              }}
            >
              {t("teacher.typeLabel")}
            </div>
            {mode === "create" ? (
              <SegmentedControl
                options={[
                  { label: t("teacher.typeOpen"), value: "open" },
                  { label: t("teacher.typeAssigned"), value: "assigned" },
                ]}
                value={values.lesson_type}
                onChange={(v) =>
                  set("lesson_type", v as "open" | "assigned")
                }
              />
            ) : (
              <div
                data-testid="lesson-form-type-readonly"
                style={{
                  padding: "var(--space-2) var(--space-3)",
                  background: "var(--surface-muted)",
                  borderRadius: "var(--radius-md)",
                  font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                  color: "var(--text-muted)",
                }}
              >
                {typeLabel}
              </div>
            )}
          </div>

          <FormField field="instructor_id" errors={fieldErrors}>
            <label
              htmlFor="lesson-form-instructor"
              style={{ font: "var(--text-label)", color: "var(--text-body)" }}
            >
              {t("teacher.instructorLabel")}
            </label>
            <select
              id="lesson-form-instructor"
              data-testid="lesson-form-instructor"
              value={values.instructor_id ?? ""}
              disabled={teachersQuery.isLoading}
              onChange={(e) =>
                set(
                  "instructor_id",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
              style={{
                width: "100%",
                height: 46,
                marginTop: 6,
                padding: "0 14px",
                background: "var(--surface-card)",
                border: `1px solid ${
                  fieldErrors.instructor_id
                    ? "var(--rust-500)"
                    : "var(--border-default)"
                }`,
                borderRadius: "var(--radius-md)",
                color: "var(--text-strong)",
                font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
              }}
            >
              {teachersQuery.isLoading && (
                <option value="">{t("common.loading")}</option>
              )}
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>
                  {displayName(teacher)}
                </option>
              ))}
            </select>
          </FormField>

          <FormField field="title" errors={fieldErrors}>
            <Input
              label={t("teacher.titleLabel")}
              placeholder={t("teacher.titlePlaceholder")}
              value={values.title}
              invalid={Boolean(fieldErrors.title)}
              onChange={(e) => set("title", e.target.value)}
              data-testid="lesson-form-title"
            />
          </FormField>

          <FormField field="description" errors={fieldErrors}>
            <Input
              label={t("teacher.descriptionLabel")}
              placeholder={t("teacher.descriptionPlaceholder")}
              value={values.description}
              invalid={Boolean(fieldErrors.description)}
              onChange={(e) => set("description", e.target.value)}
              data-testid="lesson-form-description"
            />
          </FormField>

          <FormField field="starts_at" errors={fieldErrors}>
            <Input
              label={t("teacher.startsAtLabel")}
              type="datetime-local"
              value={values.starts_at}
              invalid={Boolean(fieldErrors.starts_at)}
              onChange={(e) => set("starts_at", e.target.value)}
              data-testid="lesson-form-starts_at"
            />
          </FormField>

          <div style={{ display: "flex", gap: "var(--space-4)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FormField field="duration_minutes" errors={fieldErrors}>
                <Input
                  label={t("teacher.durationLabel")}
                  type="number"
                  min={1}
                  value={values.duration_minutes}
                  invalid={Boolean(fieldErrors.duration_minutes)}
                  onChange={(e) => set("duration_minutes", e.target.value)}
                  data-testid="lesson-form-duration_minutes"
                />
              </FormField>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <FormField field="capacity" errors={fieldErrors}>
                <Input
                  label={t("teacher.capacityLabel")}
                  type="number"
                  min={1}
                  value={values.capacity}
                  invalid={Boolean(fieldErrors.capacity)}
                  onChange={(e) => set("capacity", e.target.value)}
                  data-testid="lesson-form-capacity"
                />
              </FormField>
            </div>
          </div>

          {overCapacity && (
            <div
              role="alert"
              data-testid="lesson-form-over-capacity"
              style={{
                padding: "var(--space-2) var(--space-4)",
                background: "var(--status-warning-bg)",
                color: "var(--status-warning-fg)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
              }}
            >
              {t("teacher.overCapacity", {
                enrolled: enrolledCount,
                capacity: Number(values.capacity),
              })}
            </div>
          )}

          <FormField field="booking_cutoff_minutes" errors={fieldErrors}>
            <Input
              label={
                values.lesson_type === "assigned"
                  ? t("teacher.unenrollCutoffLabel")
                  : t("teacher.bookingCutoffLabel")
              }
              type="number"
              min={0}
              placeholder="60"
              hint={
                values.lesson_type === "assigned"
                  ? t("teacher.unenrollCutoffHint")
                  : t("teacher.bookingCutoffHint")
              }
              value={values.booking_cutoff_minutes}
              invalid={Boolean(fieldErrors.booking_cutoff_minutes)}
              onChange={(e) => set("booking_cutoff_minutes", e.target.value)}
              data-testid="lesson-form-booking_cutoff_minutes"
            />
          </FormField>

          {values.lesson_type === "open" && (
            <FormField field="booking_open_days" errors={fieldErrors}>
              <Input
                label={t("teacher.openDaysLabel")}
                type="number"
                min={0}
                placeholder="2"
                hint={t("teacher.openDaysHint")}
                value={values.booking_open_days}
                invalid={Boolean(fieldErrors.booking_open_days)}
                onChange={(e) => set("booking_open_days", e.target.value)}
                data-testid="lesson-form-booking_open_days"
              />
            </FormField>
          )}

          <FormField field="recurrence" errors={fieldErrors}>
            <Input
              label={t("teacher.repeatsWeeklyLabel")}
              type="number"
              min={1}
              max={52}
              placeholder="8"
              hint={t("teacher.repeatsWeeklyHint")}
              value={values.recurrence_occurrences}
              invalid={Boolean(fieldErrors.recurrence)}
              onChange={(e) => set("recurrence_occurrences", e.target.value)}
              data-testid="lesson-form-recurrence"
            />
          </FormField>

          {wantsPicker && (
            <div data-testid="lesson-form-students">
              <div
                style={{
                  font: "var(--text-label)",
                  color: "var(--text-body)",
                  marginBottom: 6,
                }}
              >
                {t("teacher.students")}
              </div>
              <StudentPicker
                testId="lesson-form-picker"
                students={allStudents}
                selected={selectedStudents}
                atCapacity={pickerAtCapacity}
                loading={studentsQuery.isLoading}
                requiresCredits={values.lesson_type === "open"}
                comp={values.comp}
                onAdd={(s) =>
                  set("student_ids", [...values.student_ids, s.id])
                }
                onRemove={(s) =>
                  set(
                    "student_ids",
                    values.student_ids.filter((id) => id !== s.id),
                  )
                }
              />

              {values.lesson_type === "open" && (
                <div
                  data-testid="lesson-form-comp"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    marginTop: "var(--space-3)",
                    padding: "var(--space-3) var(--space-4)",
                    background: "var(--surface-muted)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  <span
                    style={{
                      font: "var(--fw-semibold) var(--fs-sm)/1.2 var(--font-body)",
                      color: "var(--text-strong)",
                    }}
                  >
                    {t("teacher.pickerComp")}
                  </span>
                  <Switch
                    checked={values.comp}
                    onChange={(next) => set("comp", next)}
                    ariaLabel={t("teacher.pickerComp")}
                  />
                </div>
              )}
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            disabled={busy}
            data-testid="lesson-form-submit"
          >
            {busy ? t("teacher.saving") : submitLabel}
          </Button>
        </form>
      </Card>
    </div>
  );
}

/** Wraps a field and renders its API validation errors inline. */
function FormField({
  field,
  errors,
  children,
}: {
  field: string;
  errors: Record<string, string[]>;
  children: ReactNode;
}) {
  const messages = errors[field];
  return (
    <div>
      {children}
      {messages && messages.length > 0 && (
        <div
          role="alert"
          data-testid={`lesson-form-error-${field}`}
          style={{
            font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
            color: "var(--rust-700)",
            marginTop: 6,
          }}
        >
          {messages.join(", ")}
        </div>
      )}
    </div>
  );
}

/**
 * Roster view (issue #89): every student account ordered by email,
 * with live booking counts. Same card language as the lesson list.
 * The search box (issue #370) filters that roster locally by display
 * name or email, using the same matching rule as the enrollment picker.
 */
function StudentRoster({
  studentsQuery,
  onOpenStudent,
  onEditStudent,
}: {
  studentsQuery: ReturnType<typeof useStudents>;
  onOpenStudent: (student: Student) => void;
  onEditStudent: (student: Student) => void;
}) {
  const { t } = useTranslation();
  const online = useOnline();
  const students: Student[] = studentsQuery.data ?? [];
  const [query, setQuery] = useState("");
  const filteredStudents = useMemo(
    () => students.filter((student) => matchesUserSearch(student, query)),
    [students, query],
  );
  const canSearch =
    !studentsQuery.isLoading && !studentsQuery.isError && students.length > 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: "12px 20px",
      }}
    >
      {canSearch && (
        <Input
          type="search"
          label={t("teacher.rosterSearchLabel")}
          placeholder={t("teacher.rosterSearchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
          data-testid="teacher-roster-search"
        />
      )}

      {studentsQuery.isLoading && (
        <div
          data-testid="teacher-roster-loading"
          style={{
            padding: "var(--space-6)",
            textAlign: "center",
            color: "var(--text-faint)",
          }}
        >
          {t("teacher.rosterLoading")}
        </div>
      )}

      {!studentsQuery.isLoading && studentsQuery.isError && (
        online ? (
          <div
            role="alert"
            data-testid="teacher-roster-error"
            style={{
              background: "var(--status-danger-bg)",
              color: "var(--status-danger-fg)",
              padding: "var(--space-4)",
              borderRadius: "var(--radius-md)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
            }}
          >
            {studentsQuery.error?.message ?? t("teacher.rosterError")}
          </div>
        ) : (
          <OfflineErrorState />
        )
      )}

      {!studentsQuery.isLoading &&
        !studentsQuery.isError &&
        students.length === 0 && (
          <div
            data-testid="teacher-roster-empty"
            style={{
              padding: "var(--space-8) var(--space-4)",
              textAlign: "center",
              color: "var(--text-muted)",
            }}
          >
            <div
              style={{
                font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
                color: "var(--text-strong)",
                marginBottom: 8,
              }}
            >
              {t("teacher.rosterEmptyTitle")}
            </div>
            <div
              style={{
                font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
              }}
            >
              {t("teacher.rosterEmptyBody")}
            </div>
          </div>
        )}

      {canSearch && filteredStudents.length === 0 && (
        <div
          data-testid="teacher-roster-no-matches"
          style={{
            padding: "var(--space-8) var(--space-4)",
            textAlign: "center",
            color: "var(--text-muted)",
          }}
        >
          <div
            style={{
              font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
              color: "var(--text-strong)",
              marginBottom: 8,
            }}
          >
            {t("teacher.rosterNoMatchesTitle")}
          </div>
          <div
            style={{
              font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
              marginBottom: 16,
            }}
          >
            {t("teacher.rosterNoMatchesBody")}
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setQuery("")}
            data-testid="teacher-roster-clear-search"
          >
            {t("teacher.rosterClearSearch")}
          </Button>
        </div>
      )}

      {!studentsQuery.isLoading &&
        !studentsQuery.isError &&
        filteredStudents.map((student) => (
          <StudentRow
            key={student.id}
            student={student}
            onOpenDetails={() => onOpenStudent(student)}
            onEdit={() => onEditStudent(student)}
          />
        ))}
    </div>
  );
}

/** A single roster entry: identity + live booking counts + Edit. */
function StudentRow({
  student,
  onOpenDetails,
  onEdit,
}: {
  student: Student;
  onOpenDetails: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Card
      data-testid={`student-${student.id}`}
      padding="var(--space-4)"
      interactive
      role="button"
      tabIndex={0}
      aria-label={t("teacher.studentDetailOpenAria", { name: displayName(student) })}
      onClick={onOpenDetails}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpenDetails();
        }
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              font: "var(--fw-bold) var(--fs-lg)/1.1 var(--font-display)",
              color: "var(--text-strong)",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayName(student)}
          </div>
          <div
            style={{
              font: "var(--fw-medium) var(--fs-sm)/1 var(--font-body)",
              color: "var(--text-muted)",
              marginTop: 4,
            }}
          >
            {student.email}
          </div>
          <div
            style={{
              font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
              color: "var(--text-faint)",
              marginTop: 4,
            }}
          >
            {t("teacher.joined", { date: formatLongDate(student.joined_at) })}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 6,
            flex: "none",
          }}
        >
          <Badge tone="brand" dot>
            {t("teacher.upcomingBookings", {
              count: student.upcoming_bookings,
            })}
          </Badge>
          <Badge
            tone={student.credit_balance > 0 ? "neutral" : "warning"}
            data-testid={`student-${student.id}-credits`}
          >
            {t("teacher.creditsCount", { count: student.credit_balance })}
          </Badge>
          {student.membership && (
            <Badge
              tone="success"
              dot
              data-testid={`student-${student.id}-membership`}
            >
              {t("teacher.rosterMember")}
            </Badge>
          )}
          {student.blocked && (
            <Badge
              tone="danger"
              variant="solid"
              data-testid={`student-${student.id}-blocked`}
            >
              {t("teacher.studentBlockedBadge")}
            </Badge>
          )}
          <span
            data-testid={`student-${student.id}-total`}
            style={{
              font: "var(--fw-regular) var(--fs-2xs)/1 var(--font-body)",
              color: "var(--text-faint)",
            }}
          >
            {t("teacher.totalBookings", { count: student.total_bookings })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            data-testid={`student-${student.id}-edit`}
          >
            {t("teacher.editStudent")}
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Modal-ish overlay for editing a student's name / email (issue #88).
 * Same card + bottom-sheet pattern as the lesson form; only changed
 * fields are sent (PATCH semantics).
 */
function StudentEditOverlay({
  student,
  token,
  busy,
  fieldErrors,
  formError,
  onCancel,
  onSubmit,
}: {
  student: Student;
  token: string | null;
  busy: boolean;
  fieldErrors: Record<string, string[]>;
  formError: string | null;
  onCancel: () => void;
  onSubmit: (changes: StudentChanges) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(student.name ?? "");
  const [email, setEmail] = useState(student.email);
  const [noteBody, setNoteBody] = useState("");

  // Notes (issue #105) are loaded while the modal is open; adding one
  // refreshes the list in place without closing the modal.
  const notesQuery = useStudentNotes(token, student.id);
  const noteMutation = useCreateStudentNote(token, student.id);
  const notes: StudentNote[] = notesQuery.data ?? [];
  const noteError = lessonFieldErrors(noteMutation.error).body?.[0] ?? null;
  const noteNearLimit = noteBody.length >= 1800;

  function submit() {
    if (busy) return;

    const changes: StudentChanges = {};
    if (name.trim() !== (student.name ?? "")) changes.name = name.trim();
    if (email.trim() !== student.email) changes.email = email.trim();

    // Nothing changed — just close.
    if (Object.keys(changes).length === 0) {
      onCancel();
      return;
    }
    onSubmit(changes);
  }

  function addNote() {
    const body = noteBody.trim();
    if (body === "" || noteMutation.isPending) return;
    noteMutation.mutate(body, { onSuccess: () => setNoteBody("") });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("teacher.editStudentHeading")}
      data-testid="student-edit-form"
      style={SHEET_OVERLAY_STYLE}
      onClick={onCancel}
    >
      <Card
        padding="var(--space-5)"
        onClick={(e) => e.stopPropagation()}
        style={SHEET_STYLE}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "var(--space-4)",
          }}
        >
          <div
            style={{
              font: "var(--fw-extra) var(--fs-xl)/1.1 var(--font-display)",
              color: "var(--text-strong)",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            {t("teacher.editStudentHeading")}
          </div>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            {t("common.cancel")}
          </Button>
        </div>

        {formError && (
          <div
            role="alert"
            data-testid="student-edit-form-error"
            style={{
              background: "var(--status-danger-bg)",
              color: "var(--status-danger-fg)",
              padding: "var(--space-3) var(--space-4)",
              borderRadius: "var(--radius-md)",
              font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              marginBottom: "var(--space-4)",
            }}
          >
            {formError}
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <Input
            label={t("teacher.studentNameLabel")}
            placeholder={student.name ?? student.email}
            value={name}
            invalid={Boolean(fieldErrors.name)}
            hint={fieldErrors.name?.[0]}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            data-testid="student-edit-name"
          />
          <Input
            label={t("teacher.studentEmailLabel")}
            type="email"
            value={email}
            invalid={Boolean(fieldErrors.email)}
            hint={fieldErrors.email?.[0]}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
            data-testid="student-edit-email"
          />

          <Button
            type="submit"
            fullWidth
            disabled={busy}
            data-testid="student-edit-submit"
          >
            {busy ? t("teacher.studentSaving") : t("teacher.studentSave")}
          </Button>
        </form>

        {/* Notes — teacher handover (issue #105), separate from the
            profile form: adding a note never closes the modal. */}
        <div
          style={{
            marginTop: "var(--space-5)",
            borderTop: "1px solid var(--border-subtle)",
            paddingTop: "var(--space-4)",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-3)",
          }}
        >
          <div
            style={{
              font: "var(--fw-bold) var(--fs-md)/1 var(--font-display)",
              color: "var(--text-strong)",
            }}
          >
            {t("teacher.notesHeading")}
          </div>

          {notesQuery.isLoading && (
            <div style={{ color: "var(--text-faint)", fontSize: "var(--fs-sm)" }}>
              {t("common.loading")}
            </div>
          )}

          {!notesQuery.isLoading && notesQuery.isError && (
            <div
              role="alert"
              data-testid="student-notes-error"
              style={{
                background: "var(--status-danger-bg)",
                color: "var(--status-danger-fg)",
                padding: "var(--space-3) var(--space-4)",
                borderRadius: "var(--radius-md)",
                font: "var(--fw-medium) var(--fs-sm)/1.4 var(--font-body)",
              }}
            >
              {notesQuery.error?.message ?? t("teacher.noteLoadError")}
            </div>
          )}

          {!notesQuery.isLoading &&
            !notesQuery.isError &&
            notes.length === 0 && (
              <div data-testid="student-notes-empty" style={{ color: "var(--text-faint)" }}>
                {t("teacher.notesEmpty")}
              </div>
            )}

          {notes.map((note) => (
            <div
              key={note.id}
              data-testid={`student-note-${note.id}`}
              style={{
                background: "var(--surface-muted)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-3) var(--space-4)",
              }}
            >
              <div
                style={{
                  font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                {displayName(note.author)} · {formatLongDate(note.created_at)}
              </div>
              <div
                style={{
                  font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
                  color: "var(--text-body)",
                  whiteSpace: "pre-wrap",
                  // A pasted URL or email in a note must wrap, not widen the sheet.
                  overflowWrap: "anywhere",
                }}
              >
                {note.body}
              </div>
            </div>
          ))}

          {/* composer */}
          <div>
            <textarea
              value={noteBody}
              maxLength={2000}
              rows={3}
              placeholder={t("teacher.notePlaceholder")}
              onChange={(e) => setNoteBody(e.target.value)}
              disabled={noteMutation.isPending}
              data-testid="student-note-body"
              style={{
                width: "100%",
                resize: "vertical",
                boxSizing: "border-box",
                border: `1px solid ${noteError ? "var(--rust-500)" : "var(--border-default)"}`,
                borderRadius: "var(--radius-md)",
                background: "var(--surface-card)",
                color: "var(--text-strong)",
                font: "var(--text-body-base)",
                padding: "10px 14px",
                outline: "none",
              }}
            />
            {noteError && (
              <div
                role="alert"
                data-testid="student-note-error"
                style={{
                  font: "var(--fw-medium) var(--fs-xs)/1.4 var(--font-body)",
                  color: "var(--rust-700)",
                  marginTop: 6,
                }}
              >
                {noteError}
              </div>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginTop: 8,
              }}
            >
              <span style={{ flex: 1 }} />
              <span
                data-testid="student-note-count"
                style={{
                  font: "var(--fw-bold) var(--fs-2xs)/1 var(--font-mono)",
                  color: noteNearLimit ? "var(--rust-700)" : "var(--text-faint)",
                }}
              >
                {t("teacher.noteCount", { count: noteBody.length })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                onClick={addNote}
                disabled={noteMutation.isPending || noteBody.trim() === ""}
                data-testid="student-note-add"
              >
                {noteMutation.isPending ? t("teacher.noteSaving") : t("teacher.addNote")}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
